import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, HardHat, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { AccesRefuse, AlertBanner, Button, KPICard, Modal, PageHeader, Skeleton, StatusBadge, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/FormCard';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import type { StatusKey } from '@/types';
import { toast } from '@/store/toastStore';
import { listChantiers } from '@/services/chantiers';
import { listEmployes } from '@/services/rh';
import {
  type Epi, type EpiStatut, type Gravite, type Incident, type IncidentStatut, type IncidentType,
  createEpi, createIncident, deleteEpi, deleteIncident, getStatsHSE, getTableauBordHSE,
  listEpi, listIncidents, updateIncident,
} from '@/services/hse';

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const today = () => new Date().toISOString().slice(0, 10);
const NOW = new Date();

const TYPE_LABEL: Record<IncidentType, string> = {
  ACCIDENT_TRAVAIL: 'Accident du travail', PRESQUE_ACCIDENT: 'Presque-accident',
  INCIDENT_ENVIRONNEMENTAL: 'Incident environnemental', MALADIE_PRO: 'Maladie professionnelle',
};
const GRAVITE_UI: Record<Gravite, { label: string; status: StatusKey }> = {
  BENIN: { label: 'Bénin', status: 'done' }, GRAVE: { label: 'Grave', status: 'working' }, FATAL: { label: 'Fatal', status: 'stuck' },
};
const STATUT_UI: Record<IncidentStatut, { label: string; status: StatusKey }> = {
  OUVERT: { label: 'Ouvert', status: 'stuck' }, EN_COURS: { label: 'En cours', status: 'working' }, CLOS: { label: 'Clos', status: 'done' },
};
const EPI_UI: Record<EpiStatut, { label: string; status: StatusKey }> = {
  EN_SERVICE: { label: 'En service', status: 'done' }, A_RENOUVELER: { label: 'À renouveler', status: 'working' }, PERIME: { label: 'Périmé', status: 'stuck' },
};

export function HsePage() {
  const perms = usePerms('hse');
  const [tab, setTab] = useState('incidents');
  const { data: tb } = useQuery({ queryKey: ['hse-tb'], queryFn: () => getTableauBordHSE(NOW.getMonth() + 1, NOW.getFullYear()), enabled: perms.canConsulter });

  if (!perms.canConsulter) return <AccesRefuse detail="Le module HSE requiert la permission « HSE »." />;

  return (
    <>
      <PageHeader title="HSE — Sécurité & Environnement" subtitle="Incidents de chantier, EPI et indicateurs de sécurité"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'HSE' }]} />
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KPICard title="Incidents ouverts (ce mois)" value={String(tb?.incidents_ouverts_mois ?? 0)} icon={AlertTriangle} />
        <KPICard title="Jours d'arrêt (ce mois)" value={String(tb?.jours_arret_mois ?? 0)} icon={CalendarClock} />
        <KPICard title="Incidents graves / fatals" value={String(tb?.incidents_graves_fatals_mois ?? 0)} icon={AlertTriangle} accent={(tb?.incidents_graves_fatals_mois ?? 0) > 0 ? '#c0392b' : undefined} />
        <KPICard title="EPI à renouveler (30 j)" value={String(tb?.epi_a_renouveler_30j ?? 0)} icon={ShieldCheck} />
      </div>
      <Tabs className="mb-4" value={tab} onChange={setTab} tabs={[
        { key: 'incidents', label: 'Incidents' },
        { key: 'epi', label: 'EPI' },
        { key: 'stats', label: 'Statistiques' },
      ]} />
      {tab === 'incidents' && <IncidentsTab perms={perms} />}
      {tab === 'epi' && <EpiTab perms={perms} />}
      {tab === 'stats' && <StatsTab />}
    </>
  );
}

/* ------------------------------------------------------------- Incidents */
function IncidentsTab({ perms }: { perms: ReturnType<typeof usePerms> }) {
  const qc = useQueryClient();
  const [fType, setFType] = useState<IncidentType | ''>('');
  const [fGrav, setFGrav] = useState<Gravite | ''>('');
  const [fStatut, setFStatut] = useState<IncidentStatut | ''>('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ chantier_id: '', employe_concerne_id: '', date: today(), type: 'PRESQUE_ACCIDENT' as IncidentType, gravite: 'BENIN' as Gravite, description: '', jours_arret: 0, statut: 'OUVERT' as IncidentStatut, actions_correctives: '', declaration_cnas_date: '' });
  const set = (p: Partial<typeof form>) => setForm((f) => ({ ...f, ...p }));

  const { data, isLoading } = useQuery({ queryKey: ['hse-incidents', fType, fGrav, fStatut], queryFn: () => listIncidents({ type: fType || undefined, gravite: fGrav || undefined, statut: fStatut || undefined }) });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers() });
  const { data: employes } = useQuery({ queryKey: ['employes'], queryFn: () => listEmployes() });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['hse-incidents'] }); qc.invalidateQueries({ queryKey: ['hse-tb'] }); };

  const createMut = useMutation({
    mutationFn: () => createIncident({
      chantier_id: form.chantier_id || null, employe_concerne_id: form.employe_concerne_id || null,
      date: form.date, type: form.type, gravite: form.gravite, description: form.description || null,
      jours_arret: Number(form.jours_arret) || 0, statut: form.statut,
      actions_correctives: form.actions_correctives || null, declaration_cnas_date: form.declaration_cnas_date || null,
    }),
    onSuccess: () => { invalidate(); setOpen(false); toast.success('Incident signalé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const statutMut = useMutation({ mutationFn: ({ id, statut }: { id: string; statut: IncidentStatut }) => updateIncident(id, { statut }), onSuccess: () => { invalidate(); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const delMut = useMutation({ mutationFn: deleteIncident, onSuccess: () => { invalidate(); toast.success('Incident supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select className={cn(selectCls, 'w-52')} value={fType} onChange={(e) => setFType(e.target.value as IncidentType | '')}>
          <option value="">Tous types</option>{Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className={cn(selectCls, 'w-36')} value={fGrav} onChange={(e) => setFGrav(e.target.value as Gravite | '')}>
          <option value="">Toute gravité</option>{Object.entries(GRAVITE_UI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className={cn(selectCls, 'w-36')} value={fStatut} onChange={(e) => setFStatut(e.target.value as IncidentStatut | '')}>
          <option value="">Tout statut</option>{Object.entries(STATUT_UI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {perms.canModifier && <Button className="ml-auto" onClick={() => setOpen(true)}><Plus /> Signaler un incident</Button>}
      </div>

      {isLoading ? <Skeleton className="h-64 w-full" /> : (data ?? []).length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface-secondary p-10 text-center text-sm text-content-muted">Aucun incident.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-3 label-caps">Date</th><th className="px-4 py-3 label-caps">Type</th><th className="px-4 py-3 label-caps">Chantier</th>
              <th className="px-4 py-3 label-caps">Employé</th><th className="px-4 py-3 label-caps">Gravité</th><th className="px-4 py-3 text-right label-caps">Arrêt (j)</th>
              <th className="px-4 py-3 label-caps">Statut</th><th className="px-4 py-3" /></tr></thead>
            <tbody>
              {(data ?? []).map((i: Incident) => (
                <tr key={i.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 tabular-nums">{i.date}</td>
                  <td className="px-4 py-2 text-content-secondary">{TYPE_LABEL[i.type]}</td>
                  <td className="px-4 py-2 text-content-secondary">{i.chantier_nom ?? '—'}</td>
                  <td className="px-4 py-2 text-content-secondary">{i.employe_nom ?? '—'}</td>
                  <td className="px-4 py-2"><StatusBadge status={GRAVITE_UI[i.gravite].status} label={GRAVITE_UI[i.gravite].label} /></td>
                  <td className="px-4 py-2 text-right tabular-nums">{i.jours_arret}</td>
                  <td className="px-4 py-2">
                    {perms.canModifier ? (
                      <select className="h-8 rounded border border-border bg-surface-secondary px-1 text-xs" value={i.statut} onChange={(e) => statutMut.mutate({ id: i.id, statut: e.target.value as IncidentStatut })}>
                        {Object.entries(STATUT_UI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    ) : <StatusBadge status={STATUT_UI[i.statut].status} label={STATUT_UI[i.statut].label} />}
                  </td>
                  <td className="px-4 py-2 text-right">{perms.canSupprimer && <button className="text-status-stuck" onClick={() => { if (window.confirm('Supprimer cet incident ?')) delMut.mutate(i.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onOpenChange={setOpen} title="Signaler un incident" size="lg"
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={() => createMut.mutate()} loading={createMut.isPending}>Enregistrer</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Date"><Input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} /></FormField>
          <FormField label="Type">
            <select className={selectCls} value={form.type} onChange={(e) => set({ type: e.target.value as IncidentType })}>{Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          </FormField>
          <FormField label="Chantier">
            <select className={selectCls} value={form.chantier_id} onChange={(e) => set({ chantier_id: e.target.value })}>
              <option value="">— Aucun —</option>{(chantiers?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
            </select>
          </FormField>
          <FormField label="Employé concerné">
            <select className={selectCls} value={form.employe_concerne_id} onChange={(e) => set({ employe_concerne_id: e.target.value })}>
              <option value="">— Aucun —</option>{(employes?.items ?? []).map((emp) => <option key={emp.id} value={emp.id}>{emp.nom} {emp.prenom}</option>)}
            </select>
          </FormField>
          <FormField label="Gravité">
            <select className={selectCls} value={form.gravite} onChange={(e) => set({ gravite: e.target.value as Gravite })}>{Object.entries(GRAVITE_UI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
          </FormField>
          <FormField label="Jours d'arrêt"><Input type="number" step="1" value={form.jours_arret} onChange={(e) => set({ jours_arret: Number(e.target.value) })} /></FormField>
          <FormField label="Déclaration CNAS (date)"><Input type="date" value={form.declaration_cnas_date} onChange={(e) => set({ declaration_cnas_date: e.target.value })} /></FormField>
          <FormField label="Description" className="sm:col-span-2"><Input value={form.description} onChange={(e) => set({ description: e.target.value })} /></FormField>
          <FormField label="Actions correctives" className="sm:col-span-2"><Input value={form.actions_correctives} onChange={(e) => set({ actions_correctives: e.target.value })} /></FormField>
        </div>
      </Modal>
    </div>
  );
}

/* ------------------------------------------------------------------ EPI */
const EPI_TYPES = ['Casque', 'Chaussures de sécurité', 'Gants', 'Harnais', 'Lunettes', 'Masque', 'Gilet haute visibilité', 'Bouchons d\'oreilles'];

function EpiTab({ perms }: { perms: ReturnType<typeof usePerms> }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: 'Casque', employe_id: '', chantier_id: '', date_attribution: today(), date_renouvellement: '', notes: '' });
  const set = (p: Partial<typeof form>) => setForm((f) => ({ ...f, ...p }));
  const { data, isLoading } = useQuery({ queryKey: ['hse-epi'], queryFn: () => listEpi() });
  const { data: employes } = useQuery({ queryKey: ['employes'], queryFn: () => listEmployes() });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers() });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['hse-epi'] }); qc.invalidateQueries({ queryKey: ['hse-tb'] }); };

  const createMut = useMutation({
    mutationFn: () => createEpi({ type: form.type, employe_id: form.employe_id || null, chantier_id: form.chantier_id || null, date_attribution: form.date_attribution || null, date_renouvellement: form.date_renouvellement || null, notes: form.notes || null }),
    onSuccess: () => { invalidate(); setOpen(false); toast.success('EPI attribué.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({ mutationFn: deleteEpi, onSuccess: () => { invalidate(); toast.success('EPI supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  return (
    <div className="space-y-3">
      {perms.canModifier && <Button onClick={() => setOpen(true)}><Plus /> Attribuer un EPI</Button>}
      {isLoading ? <Skeleton className="h-64 w-full" /> : (data ?? []).length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface-secondary p-10 text-center text-sm text-content-muted">Aucun EPI enregistré.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-3 label-caps">Type</th><th className="px-4 py-3 label-caps">Employé</th><th className="px-4 py-3 label-caps">Chantier</th>
              <th className="px-4 py-3 label-caps">Attribué le</th><th className="px-4 py-3 label-caps">Renouvellement</th><th className="px-4 py-3 label-caps">Statut</th><th className="px-4 py-3" /></tr></thead>
            <tbody>
              {(data ?? []).map((e: Epi) => { const st = EPI_UI[e.statut_effectif ?? e.statut]; return (
                <tr key={e.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 font-medium text-content-primary">{e.type}</td>
                  <td className="px-4 py-2 text-content-secondary">{e.employe_nom ?? '—'}</td>
                  <td className="px-4 py-2 text-content-secondary">{e.chantier_nom ?? '—'}</td>
                  <td className="px-4 py-2 tabular-nums text-content-secondary">{e.date_attribution ?? '—'}</td>
                  <td className="px-4 py-2 tabular-nums">{e.date_renouvellement ?? '—'}</td>
                  <td className="px-4 py-2"><StatusBadge status={st.status} label={st.label} /></td>
                  <td className="px-4 py-2 text-right">{perms.canSupprimer && <button className="text-status-stuck" onClick={() => { if (window.confirm('Supprimer cet EPI ?')) delMut.mutate(e.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></button>}</td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onOpenChange={setOpen} title="Attribuer un EPI"
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={() => createMut.mutate()} loading={createMut.isPending}>Enregistrer</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Type d'EPI"><select className={selectCls} value={form.type} onChange={(e) => set({ type: e.target.value })}>{EPI_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></FormField>
          <FormField label="Employé">
            <select className={selectCls} value={form.employe_id} onChange={(e) => set({ employe_id: e.target.value })}>
              <option value="">— Aucun —</option>{(employes?.items ?? []).map((emp) => <option key={emp.id} value={emp.id}>{emp.nom} {emp.prenom}</option>)}
            </select>
          </FormField>
          <FormField label="Chantier">
            <select className={selectCls} value={form.chantier_id} onChange={(e) => set({ chantier_id: e.target.value })}>
              <option value="">— Aucun —</option>{(chantiers?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
            </select>
          </FormField>
          <FormField label="Date d'attribution"><Input type="date" value={form.date_attribution} onChange={(e) => set({ date_attribution: e.target.value })} /></FormField>
          <FormField label="Date de renouvellement"><Input type="date" value={form.date_renouvellement} onChange={(e) => set({ date_renouvellement: e.target.value })} /></FormField>
        </div>
      </Modal>
    </div>
  );
}

/* ----------------------------------------------------------- Statistiques */
function StatsTab() {
  const [annee, setAnnee] = useState(NOW.getFullYear());
  const { data, isLoading } = useQuery({ queryKey: ['hse-stats', annee], queryFn: () => getStatsHSE(annee) });
  const maxInc = Math.max(1, ...(data?.evolution ?? []).map((m) => m.incidents));
  return (
    <div className="space-y-4">
      <FormField label="Exercice" className="w-32"><Input type="number" step="1" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} /></FormField>
      {isLoading || !data ? <Skeleton className="h-48 w-full" /> : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KPICard title="Taux de fréquence" value={String(data.taux_frequence)} icon={AlertTriangle} />
            <KPICard title="Taux de gravité" value={String(data.taux_gravite)} icon={HardHat} />
            <KPICard title="Jours sans accident" value={data.jours_sans_accident === null ? '—' : String(data.jours_sans_accident)} icon={ShieldCheck} />
          </div>
          <AlertBanner variant="info">
            Taux de fréquence = (accidents avec arrêt × 1 000 000) / heures travaillées · Taux de gravité = (jours d'arrêt × 1 000) / heures travaillées.
            Heures travaillées sur {annee} : <b>{Number(data.heures_travaillees).toLocaleString('fr-DZ')}</b> · accidents avec arrêt : <b>{data.accidents_avec_arret}</b> · jours d'arrêt : <b>{data.jours_arret}</b>.
          </AlertBanner>
          <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
            <p className="mb-3 text-sm font-semibold">Évolution mensuelle des incidents</p>
            <div className="flex items-end gap-1.5" style={{ height: 140 }}>
              {(data.evolution ?? []).map((m) => (
                <div key={m.mois} className="flex flex-1 flex-col items-center justify-end gap-1">
                  <span className="text-[10px] tabular-nums text-content-muted">{m.incidents || ''}</span>
                  <div className="w-full rounded-t bg-brand-primary" style={{ height: `${(m.incidents / maxInc) * 100}%`, minHeight: m.incidents ? 4 : 0 }} title={`${m.incidents} incident(s), ${m.accidents_arret} avec arrêt, ${m.jours_arret} j`} />
                  <span className="text-[10px] text-content-muted">{String(m.mois).padStart(2, '0')}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
